import { useNavigate } from "react-router-dom";
import { brl, maskDoc } from "@/lib/api";
import { ShoppingCart, Trash2 } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import { Button } from "@/components/ui-kit";
import { dt } from "./SaleDialogs";

/**
 * Detalhe da venda em aberto: o carrinho guardado esperando o cliente decidir.
 *
 * Diz o que ela NÃO é, em voz alta. Numa lista onde ela aparece lado a lado com
 * venda concluída, é fácil ler o valor como faturamento — e não é: nada aqui
 * baixou estoque, gerou comissão ou entrou no caixa. O aviso vale mais que a
 * repetição dos itens.
 */
export default function HeldDetail({ held, onClose, pdvPath, onDiscard }) {
  const navigate = useNavigate();
  const itens = held.items || [];
  const bruto = itens.reduce((s, i) => s + (i.unit_price || 0) * (i.qty || 0), 0);
  const desconto = held.discount_type === "percentual"
    ? bruto * ((held.discount_value || 0) / 100)
    : (held.discount_value || 0);

  return (
    <Modal open onClose={onClose} title={held.label || "Venda em aberto"} maxWidth="max-w-lg" testId="held-detail-dialog">
      <div className="space-y-4">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="text-sm font-semibold text-amber-800">Ainda não é uma venda</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Nada de estoque, comissão, caixa ou nota fiscal foi movimentado. Ao retomar no PDV,
            preço e saldo são reconferidos contra o catálogo de agora.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500">Cliente</span>
            <p className="text-slate-900">
              {held.customer_name || "—"}
              {held.customer_document ? ` · ${maskDoc(held.customer_document)}` : ""}
            </p>
          </div>
          <div><span className="text-slate-500">Vendedor</span><p className="text-slate-900">{held.seller_name || "—"}</p></div>
          <div><span className="text-slate-500">Guardada em</span><p className="text-slate-900">{dt(held.created_at)}</p></div>
          {held.updated_at && held.updated_at !== held.created_at && (
            <div><span className="text-slate-500">Última alteração</span><p className="text-slate-900">{dt(held.updated_at)}</p></div>
          )}
        </div>

        {held.note && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {held.note}
          </div>
        )}

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Itens</p>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {itens.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">Carrinho vazio.</p>}
            {itens.map((i, k) => (
              <div key={k} className="flex justify-between px-3 py-2 text-sm">
                <span className="text-slate-700">
                  {i.qty}x {i.product_name || "Item"}{i.variant_label ? ` (${i.variant_label})` : ""}
                </span>
                <span className="font-mono text-slate-900">{brl((i.unit_price || 0) * (i.qty || 0))}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-3 space-y-1 text-sm">
          <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">{brl(bruto)}</span></div>
          {desconto > 0 && (
            <div className="flex justify-between text-red-500">
              <span>Desconto combinado{held.discount_type === "percentual" ? ` (${held.discount_value}%)` : ""}</span>
              <span className="font-mono">− {brl(desconto)}</span>
            </div>
          )}
          {/* Subtotal MENOS o desconto, calculado aqui.
              `held.total` é o `subtotal` da tabela, que só abate o desconto de
              LINHA — o desconto geral não entra nele. O modal mostrava os dois
              números lado a lado e o total ignorava o abatimento logo acima:
              1.559,80 − 140,38 aparecia como 1.559,80. */}
          <div className="flex justify-between font-semibold text-slate-900">
            <span>Total previsto</span>
            <span className="font-mono" data-testid="held-detail-total">
              {brl(Math.max(0, bruto - desconto))}
            </span>
          </div>
        </div>

        {/* Sem permissão, o modal vira leitura — e diz por quê. Mostrar botões
            que só respondem 404 é pior que não mostrar botão nenhum. */}
        {held.can_manage === false ? (
          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs text-slate-500" data-testid="held-detail-readonly">
              Esta venda foi guardada por <b>{held.seller_name || "outro operador"}</b>.
              Só quem a guardou — ou um administrador — pode retomá-la ou descartá-la.
            </p>
          </div>
        ) : (
        <div className="border-t border-slate-200 pt-4 flex flex-wrap gap-2 justify-between">
          {/* Navegação por `navigate` e não por <Link>: o Button do kit sempre
              renderiza <button>, e envolvê-lo num link produziria um <a> com
              <button> dentro — HTML inválido e alvo de clique duplicado.

              O ID vai no ESTADO da rota, que é o que faltava: antes o botão só
              trocava de tela e o PDV abria vazio, sem itens, sem cliente e sem
              desconto — o vendedor tinha que achar a venda de novo na lista "Em
              aberto". Mesmo idioma da recompra (useRepeatPurchase): estado de
              uso imediato, que não sobrevive a um F5 nem vaza para outra aba. */}
          <Button icon={ShoppingCart} onClick={() => navigate(pdvPath, { state: { resumeHeldId: held.id } })}
            data-testid="held-detail-pdv-btn">
            Retomar no PDV
          </Button>
          <Button variant="dangerSoft" icon={Trash2} onClick={onDiscard} data-testid="held-detail-discard-btn">
            Descartar
          </Button>
        </div>
        )}
      </div>
    </Modal>
  );
}
